import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import ApiCustom from "../../utils/ApiCustom";
import WelcomeCardMemberEditorModal from "../../page/admin/WelcomeCardMemberEditorModal";

/* ===================== Permissions ===================== */
const isSuperAdmin = () => String(localStorage.getItem("userId")) === "1";
const getUserPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    return raw ? JSON.parse(raw).map((p) => p.permissionSlug) : [];
  } catch {
    return [];
  }
};
const hasPermission = (slug) =>
  isSuperAdmin() || getUserPermissions().includes(slug);

/* ===================== Constants ===================== */
const FONT_TNR = `"Times New Roman", Times, serif`;
const BASE_MD_WIDTH = 768;

const TARGET_EXPORT_WIDTH = 2000;
const EXPORT_SCALE = Math.min(
  4,
  Math.max(2, TARGET_EXPORT_WIDTH / BASE_MD_WIDTH),
);

/* ===================== API ===================== */
const API = {
  listTemplates: "/welcome-card-template-show",
  getMemberOverride: (companyId) =>
    `/welcome-card-member-template-show/${companyId}`,
};

/* ===================== DEFAULT TEMPLATE (legacy fallback) ===================== */
/** QUAN TRỌNG: KHÔNG thêm `layers` ở đây để tránh schema cũ bị nhận nhầm là V2 */
const DEFAULT_SCHEMA = {
  backgroundSrc: "/welcome_user_template2.jpg",
  elements: {
    header: { top: 3, left: 0, width: 100 },
    welcome: { top: 9, left: 0, width: 100 },
    member: { top: 27, left: 0, width: 100 },
    centerLogo: { top: 43, left: 0, width: 100 },
    avatar: { top: 55, left: 15, width: 18 },
    repInfo: { top: 56, left: 42, width: 55, fontSize: 18 },
    companyInfo: { top: 70, left: 10, width: 80, fontSize: 18 },
    footer: { bottom: 4, left: 0, width: 100, fontSize: 18 },
  },
  assets: {
    topLogoSrc: "/logo192.png",
    centerLogoSrc: "/logo_welcome_v2.png",
  },
  memberBlock: {
    logoW: 160,
    logoH: 90,
    memberFontSize: 25,
  },
  texts: {
    headerLine1: "Hội Xây Dựng",
    headerLine2: "THÀNH PHỐ CẦN THƠ",
    welcomeTitle: "CHÀO MỪNG",
    memberLine1: "Là Hội viên chính thức của",
    memberLine2: "Hội Xây Dựng TP. Cần Thơ",
    repTitle: "NGƯỜI ĐẠI DIỆN THAM GIA",
    companyTitle: "THÔNG TIN DOANH NGHIỆP:",
    footerTitle: "SẢN PHẨM, DỊCH VỤ KINH DOANH:",
  },
};

const mergeSchema = (raw) => {
  const s = raw || {};
  return {
    ...DEFAULT_SCHEMA,
    ...s,
    elements: { ...DEFAULT_SCHEMA.elements, ...(s.elements || {}) },
    assets: { ...DEFAULT_SCHEMA.assets, ...(s.assets || {}) },
    memberBlock: { ...DEFAULT_SCHEMA.memberBlock, ...(s.memberBlock || {}) },
    texts: { ...DEFAULT_SCHEMA.texts, ...(s.texts || {}) },
    ...(Array.isArray(s.layers) ? { layers: s.layers } : {}),
    ...(s.schemaVersion != null ? { schemaVersion: s.schemaVersion } : {}),
  };
};

const safeParseSchema = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
};

/* ===================== Template var resolver (render real data) ===================== */
const formatDateVNStatic = (d) =>
  d ? new Date(d).toLocaleDateString("vi-VN") : "—";

const buildTemplateContext = ({ company, repUser }) => {
  const userPosition = repUser?.CompaniesHasUsers?.userPosition;

  const established =
    company?.foundedDate ||
    company?.establishedDate ||
    company?.createdAt ||
    null;

  return {
    companyName: company?.companyName || "—",
    establishedDate: established ? formatDateVNStatic(established) : "—",
    businessAddress: company?.businessAddress || company?.address || "—",
    companyPhone: company?.userPhone || company?.hotline || "—",
    companyEmail: company?.userEmail || "—",
    websiteOrHotline: company?.websiteOrHotline || "—",
    businessField: company?.businessField || "—",

    repUserFullName: repUser?.userFullName || "—",
    repUserPosition: userPosition || "—",
    repUserPhone: repUser?.userPhone || "—",
    repUserEmail: repUser?.userEmail || "—",
  };
};

const resolveTemplateText = (text, ctx) => {
  if (text == null) return "";
  const s = String(text);

  // Replace {{key}} with ctx[key]
  return s.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (m, key) => {
    if (!ctx) return m;
    const v = ctx[key];
    return v == null ? "—" : String(v);
  });
};

/* ===================== waitForImages (CORS-safe) ===================== */
async function waitForImages(root) {
  if (!root) return;

  const imgs = Array.from(root.querySelectorAll("img"));
  const bgHolders = Array.from(root.querySelectorAll("[data-bg-src]"));

  const imgPromises = imgs.map(async (img) => {
    if (!img) return;

    try {
      if (img.decode) {
        await img.decode();
        return;
      }
    } catch {
      /* ignore */
    }

    if (img.complete && img.naturalWidth > 0) return;

    await new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
  });

  const bgPromises = bgHolders.map((el) => {
    const url = el.getAttribute("data-bg-src");
    if (!url) return Promise.resolve();
    return new Promise((resolve) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = resolve;
      i.onerror = resolve;
      i.src = url;
    });
  });

  await Promise.all([...imgPromises, ...bgPromises]);
}

/* ===================== ScaledStage ===================== */
function useScaledStage(baseWidth = BASE_MD_WIDTH) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [frameSize, setFrameSize] = useState({ w: baseWidth, h: 0 });

  const update = () => {
    const availW = outerRef.current?.clientWidth || baseWidth;
    const s = Math.min(1, availW / baseWidth);
    setScale(s);

    if (innerRef.current) {
      const h = innerRef.current.offsetHeight || 0;
      setFrameSize({ w: baseWidth * s, h: h * s });
    }
  };

  useLayoutEffect(() => {
    update();

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (outerRef.current && ro) ro.observe(outerRef.current);
    if (innerRef.current && ro) ro.observe(innerRef.current);

    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseWidth]);

  return { outerRef, innerRef, scale, frameSize };
}

function ScaledStage({ children }) {
  const { outerRef, innerRef, scale, frameSize } = useScaledStage();

  return (
    <div ref={outerRef} style={{ width: "100%" }}>
      <div
        style={{
          width: frameSize.w,
          height: frameSize.h,
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <div
          ref={innerRef}
          style={{
            width: BASE_MD_WIDTH,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/* ===================== building blocks ===================== */
const pickLogo = (c) =>
  c?.userAvatar || c?.logo || c?.companyAvatar || c?.avatar || c?.logoUrl || "";

const fallbackAvatarUrlStatic = (() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
    <rect width="100%" height="100%" rx="100" fill="#0ea5e9"/>
    <text x="50%" y="55%" text-anchor="middle" font-size="80" fill="#fff">U</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
})();

function CircleBox({ style, imgSrc, mode = "cover" }) {
  if (!imgSrc) return null;

  return (
    <div className="absolute" style={style}>
      <div style={{ width: "100%", paddingTop: "100%", position: "relative" }}>
        <div
          data-bg-src={imgSrc}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            overflow: "hidden",
            backgroundColor: "#fff",
            backgroundImage: `url("${imgSrc}")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: mode,
          }}
        />
      </div>
    </div>
  );
}

function LogoInline({ imgSrc, width = 180, height = 100 }) {
  if (!imgSrc) return null;

  return (
    <div
      style={{
        width,
        height,
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <img
        src={imgSrc}
        alt=""
        crossOrigin="anonymous"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          display: "block",
        }}
      />
    </div>
  );
}

/* ===================== V2 detection ===================== */
const isSchemaV2 = (raw) => {
  if (!raw) return false;
  if (Number(raw.schemaVersion || 0) >= 2) return true;
  if (Array.isArray(raw.layers) && raw.layers.length > 0) return true;
  return false;
};

/* ===================== V2 helpers ===================== */
const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

function normalizeV2Layer(layer) {
  const type = layer?.type === "image" ? "image" : "text";
  const st = layer?.style || {};
  return {
    id: String(layer?.id || ""),
    type,
    left: toNum(layer?.left, 0),
    width: toNum(layer?.width, 10),
    height: toNum(layer?.height, 6),
    zIndex: toNum(layer?.zIndex, 1),
    top: layer?.bottom != null ? undefined : toNum(layer?.top, 0),
    bottom: layer?.bottom != null ? toNum(layer?.bottom, 0) : undefined,

    text: type === "text" ? String(layer?.text ?? "") : undefined,

    src: type === "image" ? String(layer?.src ?? "") : undefined,
    sourceKey: type === "image" ? String(layer?.sourceKey ?? "") : undefined,
    fit:
      type === "image"
        ? layer?.fit === "contain"
          ? "contain"
          : "cover"
        : undefined,
    circle: type === "image" ? !!layer?.circle : undefined,

    style:
      type === "text"
        ? {
            fontFamily: st.fontFamily ?? FONT_TNR,
            fontSize: toNum(st.fontSize, 18),
            fontWeight: String(st.fontWeight ?? "400"),
            color: st.color ?? "#ffffff",
            align: st.align ?? "left",
            uppercase: !!st.uppercase,
            lineHeight: toNum(st.lineHeight, 1.25),
          }
        : undefined,
  };
}

/* ===================== PageSingleV2 ===================== */
function PageSingleV2({ schema, company, repUser }) {
  const s = useMemo(() => mergeSchema(schema), [schema]);
  const layers = useMemo(() => {
    const raw = Array.isArray(schema?.layers) ? schema.layers : [];
    return raw
      .map(normalizeV2Layer)
      .sort((a, b) => Number(a.zIndex) - Number(b.zIndex));
  }, [schema]);

  const ctx = useMemo(
    () => buildTemplateContext({ company, repUser }),
    [company, repUser],
  );

  const bgRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(0);

  const measure = () => {
    const h = bgRef.current?.offsetHeight || 0;
    if (h && h !== cardHeight) setCardHeight(h);
  };

  useLayoutEffect(() => {
    measure();
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => measure())
        : null;
    if (ro && bgRef.current) ro.observe(bgRef.current);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveSrc = (layer) => {
    const key = String(layer?.sourceKey || "").trim();
    if (key === "company.logo") return pickLogo(company) || "";
    if (key === "rep.avatar")
      return repUser?.userAvatar || fallbackAvatarUrlStatic;
    if (key === "asset.topLogoSrc") return s?.assets?.topLogoSrc || "";
    if (key === "asset.centerLogoSrc") return s?.assets?.centerLogoSrc || "";
    return layer?.src || "";
  };

  const cardH = Number(cardHeight || 0);

  return (
    <div className="relative" style={{ width: BASE_MD_WIDTH }}>
      <img
        ref={bgRef}
        src={s.backgroundSrc || DEFAULT_SCHEMA.backgroundSrc}
        className="block w-full h-auto"
        alt=""
        crossOrigin="anonymous"
        draggable={false}
        onLoad={measure}
      />

      {layers.map((layer) => {
        const hasBottom = layer.bottom != null;

        const leftPx = (BASE_MD_WIDTH * toNum(layer.left, 0)) / 100;
        const widthPx = (BASE_MD_WIDTH * toNum(layer.width, 10)) / 100;

        const heightPx =
          layer.type === "image" && layer.circle
            ? widthPx
            : cardH
              ? (cardH * toNum(layer.height, 6)) / 100
              : (BASE_MD_WIDTH * toNum(layer.height, 6)) / 100;

        const topPx =
          !hasBottom && cardH ? (cardH * toNum(layer.top, 0)) / 100 : 0;
        const bottomPx =
          hasBottom && cardH ? (cardH * toNum(layer.bottom, 0)) / 100 : 0;

        const wrapperStyle = {
          position: "absolute",
          left: `${leftPx}px`,
          width: `${widthPx}px`,
          height: `${heightPx}px`,
          zIndex: layer.zIndex ?? 1,
          ...(hasBottom ? { bottom: `${bottomPx}px` } : { top: `${topPx}px` }),
        };

        if (layer.type === "image") {
          const src = resolveSrc(layer);
          return (
            <div key={layer.id} style={wrapperStyle}>
              <div
                data-bg-src={src}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: layer.circle ? "9999px" : "12px",
                  overflow: "hidden",
                  backgroundImage: src ? `url("${src}")` : "none",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  backgroundSize: layer.fit === "contain" ? "contain" : "cover",
                  backgroundColor: "transparent",
                }}
              />
            </div>
          );
        }

        const st = layer.style || {};
        const resolvedText = resolveTemplateText(layer.text || "", ctx);

        return (
          <div key={layer.id} style={wrapperStyle}>
            <div
              style={{
                width: "100%",
                height: "100%",
                fontFamily: st.fontFamily ?? FONT_TNR,
                fontSize: st.fontSize ?? 18,
                fontWeight: st.fontWeight ?? "400",
                color: st.color ?? "#fff",
                textAlign: st.align ?? "left",
                lineHeight: st.lineHeight ?? 1.25,
                textTransform: st.uppercase ? "uppercase" : "none",
                whiteSpace: "pre-wrap",
                overflow: "hidden",
              }}
            >
              {resolvedText}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ===================== PageSingleLegacy (FIX centerLogo position) ===================== */
function PageSingleLegacy({
  company,
  repUser,
  userPosition,
  foundedDate,
  formatDateVN,
  pickLogo: pickLogoFn,
  fallbackAvatarUrl,
  schema,
}) {
  const merged = useMemo(() => mergeSchema(schema), [schema]);

  const avatar = repUser?.userAvatar || fallbackAvatarUrl;
  const logo = pickLogoFn(company);

  // dynamic spacing giữa tên công ty và member block
  const rootRef = useRef(null);
  const nameRef = useRef(null);
  const [memberTopPx, setMemberTopPx] = useState(null);

  const recomputeLayout = () => {
    const root = rootRef.current;
    const nameEl = nameRef.current;
    if (!root || !nameEl) return;

    const rootH = root.offsetHeight || 0;
    if (!rootH) return;

    const memberTopPct = merged?.elements?.member?.top ?? 27;
    const baseMemberTop = rootH * (memberTopPct / 100);

    const cs = window.getComputedStyle(nameEl);
    const lh = parseFloat(cs.lineHeight) || 0;
    if (!lh) {
      setMemberTopPx(null);
      return;
    }

    const targetNameH = lh * 3;
    const actualNameH = nameEl.offsetHeight || 0;

    const diff = targetNameH - actualNameH;
    const adjust = Math.max(-40, Math.min(70, diff * 0.85));

    setMemberTopPx(baseMemberTop - adjust);
  };

  useLayoutEffect(() => {
    const raf1 = requestAnimationFrame(() =>
      requestAnimationFrame(recomputeLayout),
    );

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(recomputeLayout)
        : null;
    if (ro && rootRef.current) ro.observe(rootRef.current);

    return () => {
      cancelAnimationFrame(raf1);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.companyName, JSON.stringify(merged?.elements || {})]);

  const el = merged.elements;
  const t = merged.texts;

  const absPct = ({ top, left, width }) => ({
    left: `${left ?? 0}%`,
    top: `${top ?? 0}%`,
    width: `${width ?? 100}%`,
  });

  const absBottomPct = ({ bottom, left, width }) => ({
    left: `${left ?? 0}%`,
    bottom: `${bottom ?? 0}%`,
    width: `${width ?? 100}%`,
  });

  const listStyle = { padding: 0, margin: 0, listStyle: "none" };
  const itemStyle = {
    display: "flex",
    gap: 6,
    lineHeight: 1.45,
    fontWeight: 300,
  };
  const dotStyle = { fontSize: 18, marginTop: 2 };

  // FIX: centerLogo theo đúng kiểu layer V2 (tỷ lệ theo thiệp), không dùng img size cứng nữa
  // - giữ top theo el.centerLogo.top (mẫu gốc), còn left/width/height dùng chuẩn V2.
  const centerLogoSrc = merged?.assets?.centerLogoSrc || "";
  const centerLogoTop = el?.centerLogo?.top ?? 43;
  const centerLogoRect = {
    position: "absolute",
    left: "42%",
    top: `${centerLogoTop}%`,
    width: "16%",
    height: "20%",
    zIndex: 50,
  };

  return (
    <div ref={rootRef} className="relative" style={{ width: BASE_MD_WIDTH }}>
      <img
        src={merged.backgroundSrc}
        className="block w-full h-auto"
        alt=""
        onLoad={recomputeLayout}
        crossOrigin="anonymous"
      />

      {/* Header */}
      <div
        className="absolute flex justify-center text-white"
        style={absPct(el.header)}
      >
        <div className="flex items-center gap-3">
          <img
            src={merged.assets.topLogoSrc}
            alt=""
            style={{ width: 50, height: 50 }}
            crossOrigin="anonymous"
          />
          <div className="text-center" style={{ fontFamily: FONT_TNR }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{t.headerLine1}</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{t.headerLine2}</div>
          </div>
        </div>
      </div>

      {/* Welcome */}
      <div
        className="absolute text-center text-white px-14 pt-2"
        style={absPct(el.welcome)}
      >
        <div style={{ fontSize: 38, fontWeight: 400 }}>{t.welcomeTitle}</div>
        <div
          ref={nameRef}
          style={{
            marginTop: 8,
            fontSize: 27,
            lineHeight: "32px",
            fontWeight: 700,
            color: "#facc15",
            textTransform: "uppercase",
            fontFamily: FONT_TNR,
          }}
        >
          {company?.companyName}
        </div>
      </div>

      {/* Member block */}
      <div
        className="absolute text-center text-white"
        style={{
          left: `${el.member.left ?? 0}%`,
          width: `${el.member.width ?? 100}%`,
          top:
            memberTopPx != null
              ? `${memberTopPx}px`
              : `${el.member.top ?? 27}%`,
        }}
      >
        <LogoInline
          imgSrc={logo}
          width={merged.memberBlock.logoW}
          height={merged.memberBlock.logoH}
        />
        <div
          style={{
            marginTop: 10,
            fontSize: merged.memberBlock.memberFontSize,
            fontWeight: 600,
          }}
        >
          {t.memberLine1}
        </div>
        <div
          style={{
            fontSize: merged.memberBlock.memberFontSize,
            fontWeight: 600,
          }}
        >
          {t.memberLine2}
        </div>
      </div>

      {/* Center logo (FIXED) */}
      <div style={centerLogoRect}>
        <div
          data-bg-src={centerLogoSrc}
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundImage: centerLogoSrc ? `url("${centerLogoSrc}")` : "none",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain",
            backgroundColor: "transparent",
          }}
        />
      </div>

      {/* Avatar */}
      <CircleBox style={absPct(el.avatar)} imgSrc={avatar} />

      {/* Rep info */}
      <div
        className="absolute text-white"
        style={{ ...absPct(el.repInfo), fontSize: el.repInfo?.fontSize ?? 18 }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{t.repTitle}</div>
        <ul style={listStyle}>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>Người đại diện: {repUser?.userFullName}</span>
          </li>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>Chức vụ: {userPosition}</span>
          </li>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>SĐT: {repUser?.userPhone || "—"}</span>
          </li>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>Email: {repUser?.userEmail || "—"}</span>
          </li>
        </ul>
      </div>

      {/* Company info */}
      <div
        className="absolute text-white"
        style={{
          ...absPct(el.companyInfo),
          fontSize: el.companyInfo?.fontSize ?? 18,
        }}
      >
        <div style={{ fontWeight: 700 }}>{t.companyTitle}</div>
        <ul style={listStyle}>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>
              Ngày thành lập:{" "}
              {formatDateVN(foundedDate || company?.establishedDate)}
            </span>
          </li>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>Địa chỉ: {company?.businessAddress || company?.address}</span>
          </li>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>Điện thoại: {company?.userPhone || company?.hotline}</span>
          </li>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>Email: {company?.userEmail}</span>
          </li>
          <li style={itemStyle}>
            <span style={dotStyle}>•</span>
            <span>Website: {company?.websiteOrHotline}</span>
          </li>
        </ul>
      </div>

      {/* Footer */}
      <div
        className="absolute text-center text-white px-4"
        style={{
          ...absBottomPct(el.footer),
          fontSize: el.footer?.fontSize ?? 18,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: el.footer?.fontSize ?? 18 }}>
          {t.footerTitle}
        </div>
        <div style={{ fontWeight: 300, fontSize: el.footer?.fontSize ?? 18 }}>
          {company?.businessField || "—"}
        </div>
      </div>
    </div>
  );
}

/* ===================== PageSingle wrapper ===================== */
function PageSingle(props) {
  const v2 = isSchemaV2(props?.schema);
  if (v2)
    return (
      <PageSingleV2
        schema={props.schema}
        company={props.company}
        repUser={props.repUser}
      />
    );
  return <PageSingleLegacy {...props} />;
}

/* ===================== API helpers ===================== */
const extractList = (res) => {
  const payload = res?.data?.data ?? res?.data ?? null;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const pickActiveTemplate = (items) => {
  const list = Array.isArray(items) ? items : [];
  const actives = list.filter((x) => !!x?.isActive);
  const pool = actives.length ? actives : list;
  if (!pool.length) return null;
  return pool
    .slice()
    .sort((a, b) => Number(b?.version ?? 0) - Number(a?.version ?? 0))[0];
};

const getSchemaFromOverrideResponse = (res) => {
  const d = res?.data?.data ?? res?.data ?? null;
  const raw = d?.schema ?? d;
  return safeParseSchema(safeParseSchema(raw));
};

/* ===================== Main: UserPDFModal ===================== */
const UserPDFModal = ({ isOpen, onClose, company }) => {
  const [repUser, setRepUser] = useState(null);

  const [busyPNG, setBusyPNG] = useState(false);
  const [busyPDF, setBusyPDF] = useState(false);

  const [loadingSchema, setLoadingSchema] = useState(false);
  const [schema, setSchema] = useState(null);

  const [openEditor, setOpenEditor] = useState(false);

  const exportRef = useRef(null);
  const companyId = company?.id;

  const formatDateVN = (d) =>
    d ? new Date(d).toLocaleDateString("vi-VN") : "—";
  const fallbackAvatarUrl = useMemo(() => fallbackAvatarUrlStatic, []);

  // 1) chọn người đại diện
  useEffect(() => {
    if (!isOpen) return;

    const picked =
      company?.users?.find(
        (u) => u?.CompaniesHasUsers?.isUserRepresentOfCompany,
      ) ||
      company?.users?.[0] ||
      null;

    setRepUser(picked);
  }, [isOpen, company]);

  // alive ref chống setState sau unmount
  const aliveRef = useRef(false);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // 2) load schema: override -> active -> default
  const loadSchema = async () => {
    if (!isOpen) return;

    setLoadingSchema(true);
    try {
      // A) override hội viên
      if (companyId) {
        try {
          const resOv = await ApiCustom.get(API.getMemberOverride(companyId));
          const ovSchemaRaw = getSchemaFromOverrideResponse(resOv);

          if (!aliveRef.current) return;

          if (ovSchemaRaw) {
            setSchema(mergeSchema(ovSchemaRaw));
            return;
          }
        } catch {
          // 404 => ignore
        }
      }

      // B) active template
      try {
        const res = await ApiCustom.get(API.listTemplates);
        const list = extractList(res);
        const active = pickActiveTemplate(list);
        const activeSchemaRaw = safeParseSchema(
          safeParseSchema(active?.schema),
        );

        if (!aliveRef.current) return;

        setSchema(activeSchemaRaw ? mergeSchema(activeSchemaRaw) : null);
      } catch {
        if (!aliveRef.current) return;
        setSchema(null);
      }
    } finally {
      if (aliveRef.current) setLoadingSchema(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyId]);

  const exportPNG = async () => {
    if (!exportRef.current) return;

    try {
      setBusyPNG(true);
      await waitForImages(exportRef.current);

      const canvas = await html2canvas(exportRef.current, {
        scale: EXPORT_SCALE,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });

      canvas.toBlob(
        (blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "chao_mung.png";
          a.click();
          URL.revokeObjectURL(url);
        },
        "image/png",
        1,
      );
    } finally {
      setBusyPNG(false);
    }
  };

  const exportPDF = async () => {
    if (!exportRef.current) return;

    try {
      setBusyPDF(true);
      await waitForImages(exportRef.current);

      const canvas = await html2canvas(exportRef.current, {
        scale: EXPORT_SCALE,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;

      const y = imgH < pageH ? (pageH - imgH) / 2 : 0;
      const h = Math.min(imgH, pageH);

      pdf.addImage(imgData, "PNG", 0, y, imgW, h, undefined, "FAST");
      pdf.save("chao_mung.pdf");
    } finally {
      setBusyPDF(false);
    }
  };

  if (!isOpen) return null;

  const schemaToUse = schema || DEFAULT_SCHEMA;

  const closeEditorAndReload = async () => {
    setOpenEditor(false);
    await loadSchema();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="bg-white w-5/6 max-h-[90vh] overflow-y-auto rounded-lg p-4">
        {loadingSchema && (
          <div className="text-sm text-gray-500 mb-2">Đang tải mẫu thiệp…</div>
        )}

        <ScaledStage>
          <PageSingle
            company={company}
            repUser={repUser}
            userPosition={repUser?.CompaniesHasUsers?.userPosition}
            foundedDate={company?.foundedDate}
            formatDateVN={formatDateVN}
            pickLogo={pickLogo}
            fallbackAvatarUrl={fallbackAvatarUrl}
            schema={schemaToUse}
          />
        </ScaledStage>

        {/* Export hidden (must match preview EXACTLY) */}
        <div
          ref={exportRef}
          style={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: BASE_MD_WIDTH,
            pointerEvents: "none",
          }}
        >
          <PageSingle
            company={company}
            repUser={repUser}
            userPosition={repUser?.CompaniesHasUsers?.userPosition}
            foundedDate={company?.foundedDate}
            formatDateVN={formatDateVN}
            pickLogo={pickLogo}
            fallbackAvatarUrl={fallbackAvatarUrl}
            schema={schemaToUse}
          />
        </div>

        <div className="flex justify-end gap-3 mt-4 flex-wrap">
          <button onClick={onClose} className="btn btn-outline">
            Đóng
          </button>

          {hasPermission("WELCOME_CARD_UPDATE") && (
            <button
              onClick={() => setOpenEditor(true)}
              className="btn btn-warning"
              title="Chỉnh sửa thiệp riêng cho hội viên này"
              disabled={!companyId}
            >
              <PencilSquareIcon className="w-5 h-5 mr-1" />
              Chỉnh sửa thiệp
            </button>
          )}

          <button
            onClick={exportPNG}
            disabled={busyPNG}
            className="btn btn-info"
          >
            PNG
          </button>

          <button
            onClick={exportPDF}
            disabled={busyPDF}
            className="btn btn-success"
          >
            PDF
          </button>
        </div>
      </div>

      <WelcomeCardMemberEditorModal
        isOpen={openEditor}
        onClose={closeEditorAndReload}
        company={company}
        repUser={repUser}
      />
    </div>
  );
};

export default UserPDFModal;
